import React from 'react';
import StarIcon from '../icons/StarIcon';
import BriefcaseIcon from '../icons/BriefcaseIcon';
import LocationIcon from '../icons/LocationIcon';
import CheckIcon from '../icons/CheckIcon';
import ShareIcon from '../icons/ShareIcon';
import NotAvailable from '../Common/NotAvailable';
import ProviderRating from './ProviderRating';

interface Education {
  institute: string;
  degree: string;
  year: string;
}

interface ProviderCardProps {
  provider: {
    name: string;
    location: string | null;
    rating: number;
    avgRating?: number | null;
    reviewCount?: number;
    specialty: string | null;
    status: string;
    profileImage?: string | null;
    profile_completion_percentage?: number;
    business_name?: string;
    user_profile_image_url?: string;
    is_certified?: number;
    education?: Education[];
    years_of_experience?: number | null;
    user_email?: string;
  };
  isOwnProfile: boolean;
  endorsementsGiven: number;
  onCompleteProfile: () => void;
  onEndorse: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  isOwnProfile,
  endorsementsGiven,
  onCompleteProfile,
  onEndorse,
}) => {
  return (
    <div className="bg-white rounded-lg p-4 sm:p-6 lg:p-8 pt-16 sm:pt-20 text-center shadow-lg relative">
      {/* Profile Image - Positioned to overlap */}
      <div className="absolute -top-12 sm:-top-16 left-1/2 transform -translate-x-1/2">
        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-300">
          {provider.profileImage ? (
            <img
              src={provider.profileImage}
              alt={provider.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-xl sm:text-2xl font-['Open_Sans'] font-semibold text-gray-600">
                {provider.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Certified/Uncertified Badge */}
      <div className="mb-4">
        <div
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-['Open_Sans'] font-semibold ${
            provider.status === 'Certified'
              ? 'bg-[#c6a664] text-white'
              : 'bg-[#f8f6f2] text-[#c6a664]'
          }`}
        >
          {provider.status === 'Certified' && (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.3333 4L6 11.3333L2.66667 8"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {provider.status}
        </div>
      </div>

      {/* Provider Info */}
      <h2 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-3 sm:mb-4">{provider.name}</h2>

      <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 text-[#1f1f1f]">
        <div className="flex items-center justify-center gap-2">
          <StarIcon />
          <span className="font-['Open_Sans'] text-xs sm:text-sm">{provider.rating} Clout Score</span>
        </div>
        <div className="flex items-center justify-center gap-2 font-['Open_Sans'] text-xs sm:text-sm">
          <span>Rating:</span>
          <ProviderRating avgRating={provider.avgRating} reviewCount={provider.reviewCount} />
        </div>
        <div className="flex items-center justify-center gap-2">
          <BriefcaseIcon />
          <span className="font-['Open_Sans'] text-xs sm:text-sm">
            {provider.specialty || <NotAvailable />}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2">
          <LocationIcon />
          <span className="font-['Open_Sans'] text-xs sm:text-sm">
            {provider.location || <NotAvailable />}
          </span>
        </div>
      </div>

      {/* Action Buttons or Profile Completion */}
      {isOwnProfile ? (
        <>
          {/* Profile Completion Section */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-[#eaeaea]">
            <h3 className="font-['Open_Sans'] font-semibold text-sm sm:text-base text-[#1f1f1f] mb-2">
              Complete your Profile
            </h3>
            <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472] mb-3 sm:mb-4">
              {100 - (provider.profile_completion_percentage || 0)}% to CLOUT Certification
            </p>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-3 sm:mb-4">
              <div
                className="bg-[#c6a664] h-2 rounded-full transition-all duration-300"
                style={{ width: `${provider.profile_completion_percentage || 0}%` }}
              />
            </div>
            <p className="font-['Open_Sans'] text-xs text-[#767472] mb-4 sm:mb-6">
              {provider.profile_completion_percentage || 0}% complete
            </p>

            {/* Completion Checklist */}
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 ${
                    provider.business_name ? 'border-[#c6a664] bg-[#c6a664]' : 'border-gray-300'
                  } flex items-center justify-center`}
                >
                  {provider.business_name && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span
                  className={`font-['Open_Sans'] text-sm ${
                    provider.business_name ? 'text-[#1f1f1f] line-through' : 'text-[#767472]'
                  }`}
                >
                  Add Basic Details
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 ${
                    provider.user_profile_image_url
                      ? 'border-[#c6a664] bg-[#c6a664]'
                      : 'border-gray-300'
                  } flex items-center justify-center`}
                >
                  {provider.user_profile_image_url && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span
                  className={`font-['Open_Sans'] text-sm ${
                    provider.user_profile_image_url
                      ? 'text-[#1f1f1f] line-through'
                      : 'text-[#767472]'
                  }`}
                >
                  Add Profile Picture
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 ${
                    provider.education && provider.education.length > 0
                      ? 'border-[#c6a664] bg-[#c6a664]'
                      : 'border-gray-300'
                  } flex items-center justify-center`}
                >
                  {provider.education && provider.education.length > 0 && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span
                  className={`font-['Open_Sans'] text-sm ${
                    provider.education && provider.education.length > 0
                      ? 'text-[#1f1f1f] line-through'
                      : 'text-[#767472]'
                  }`}
                >
                  Add Education
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 ${
                    provider.years_of_experience && provider.years_of_experience > 0
                      ? 'border-[#c6a664] bg-[#c6a664]'
                      : 'border-gray-300'
                  } flex items-center justify-center`}
                >
                  {provider.years_of_experience && provider.years_of_experience > 0 && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span
                  className={`font-['Open_Sans'] text-sm ${
                    provider.years_of_experience && provider.years_of_experience > 0
                      ? 'text-[#1f1f1f] line-through'
                      : 'text-[#767472]'
                  }`}
                >
                  Add Experience
                </span>
              </div>

              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 ${
                    endorsementsGiven > 0 ? 'border-[#c6a664] bg-[#c6a664]' : 'border-gray-300'
                  } flex items-center justify-center`}
                >
                  {endorsementsGiven > 0 && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span
                  className={`font-['Open_Sans'] text-sm ${
                    endorsementsGiven > 0 ? 'text-[#1f1f1f] line-through' : 'text-[#767472]'
                  }`}
                >
                  Endorse a Provider
                </span>
              </div>
            </div>

            {/* Complete Profile Button */}
            <button
              onClick={onCompleteProfile}
              className="w-full px-4 py-2 bg-white text-[#1f1f1f] border border-[#1f1f1f] font-['Ovo'] text-sm uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors"
            >
              COMPLETE MY PROFILE
            </button>
          </div>
        </>
      ) : (
        <div className="flex gap-3 justify-center">
          <button
            onClick={onEndorse}
            className="flex-1 px-4 py-2 bg-white text-[#1f1f1f] border border-[#c6a664] font-['Ovo'] text-sm uppercase hover:bg-[#c6a664] hover:text-white transition-colors flex items-center justify-center gap-2 rounded"
          >
            <CheckIcon />
            ENDORSE
          </button>
          <button className="p-2 bg-white border border-[#c6a664] hover:bg-[#c6a664] hover:text-white transition-colors rounded">
            <ShareIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProviderCard;
